import React, { useState } from 'react';
import { Text, ToastQueue } from '@adobe/react-spectrum';
import moment from 'moment';
import classNames from 'classnames';
import Avatar from 'app/components/SlideOutPanel/DACIContacts/Avatar';
import { QuillEditor } from 'app/components/RsdForms/CreateMoment/components/QuillEditor';
import { isEmptyHtml } from 'app/components/SlideOutPanel/CommentPanel/utils';
import { getEmployeePhotoUrl } from './employeePhotoUrl';
import { FeedbackThreadAuthor } from '../types';
import {
  patchFeedbackThreadEntry,
  AlreadyNotifiedError,
  FeedbackThreadEntryPatchResponse
} from '../feedbackThreadApi';
import formFieldStyles from 'app/components/RsdForms/CreateMoment/FormField.module.scss';
import { ReadMoreContent } from './ReadMoreContent';
import styles from './FeedbackSlideOutBody.module.scss';

interface FeedbackThreadEntryCardProps {
  label: string;
  /**
   * The id to PATCH against - `requestorNoteId` for a Requestor Note,
   * `feedbackId` for a Feedback entry. These are NOT interchangeable (a note
   * has no `feedbackId` field at all) - see feedbackThreadApi.ts's own
   * comment on `patchFeedbackThreadEntry` for why the endpoint's path
   * segment is named `feedbackId` regardless of which entry type it edits.
   */
  entryId: string;
  author: FeedbackThreadAuthor;
  content: string;
  sentAt: string;
  bordered?: boolean;
  /**
   * Whether clicking the content opens the rich-text editor - defaults to
   * true for notes without an explicit flag. Both entry types pass their
   * API `editable` field when available.
   */
  editable?: boolean;
  stacked?: boolean;
}

/**
 * One "REQUESTOR NOTE"/"FEEDBACK" card in the Feedback slide-out body
 * (Figma nodes 18738:304421/18738:304444, file hBnevR1MeKGrm062iaGE1P) -
 * both are the exact same layout (label + divider + avatar/name/role, then
 * the entry's own rich HTML content + sent-on timestamp); only the section
 * label and the Requestor Note's extra 2px border differ, so this one
 * component renders both rather than duplicating the layout twice.
 *
 * `content` is HTML (see types.ts's file-level comment on this section) -
 * read-only display uses a plain `dangerouslySetInnerHTML`, the same
 * pattern Overview.tsx already uses for its own saved rich-text fields.
 * Clicking that content switches to the same `QuillEditor` component
 * NewFeedbackEditor.tsx uses (and Overview's own rich-text fields use) -
 * the same read/edit toggle Overview.tsx itself uses (conditionally
 * rendering either the plain HTML view or the editor, controlled by local
 * state), not a new editing mechanism. `formFieldStyles.quillWrapper`/
 * `.fullEditor` (see NewFeedbackEditor.tsx's own comment) give the editor
 * the exact same white toolbar/background/border Overview's own editor
 * has, instead of this card's grey background showing through it. Cancel/Save
 * reuse `.entryCancelButton`/`.sendButton`, the exact styles Overview's own
 * per-field Cancel/Save buttons use.
 *
 * Save requires non-empty rich text, using NewFeedbackEditor's validation.
 * Save calls `patchFeedbackThreadEntry` (see feedbackThreadApi.ts) - the
 * same optimistic-close-then-revert-on-failure shape
 * FigmaPixelPerfectOverviewField.tsx/Overview.tsx already use for their own
 * rich-text field saves: the editor closes and the new content displays
 * immediately on Save (not gated on the PATCH resolving), and on failure the
 * content reverts to what it was before this edit plus a `ToastQueue.negative`
 * error - the editor is NOT reopened, matching Overview's own behavior
 * exactly. `isSaving` only guards against a duplicate PATCH firing before
 * the first one resolves (e.g. a fast double click); it isn't surfaced as
 * its own visible loading state, since Overview's own field save has none
 * either (the same optimistic close is one). A 401/403 failure skips this
 * component's own toast (see `AlreadyNotifiedError`'s doc comment in
 * feedbackThreadApi.ts) since the centralized auth handler already showed
 * one - content still reverts either way.
 *
 * Reuses the existing Avatar/getEmployeePhotoUrl pairing every other cell
 * in this feature already uses for a person's photo. Long content gets
 * Overview's own Read More/Read Less treatment via ReadMoreContent.tsx -
 * see that file's own comment on why it's the same mechanism/styling
 * scoped to one piece of content instead of Overview's own multi-field
 * map.
 */
export function FeedbackThreadEntryCard({
  label,
  entryId,
  author,
  content,
  sentAt,
  bordered = false,
  editable = true,
  stacked = false
}: FeedbackThreadEntryCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [committedContent, setCommittedContent] = useState(content);
  const [draftContent, setDraftContent] = useState(content);
  const canSave = !isEmptyHtml(draftContent) && !isSaving;
  // Not rendered by this card today (no "(edited)" affordance exists yet in
  // this Figma), but captured from the PATCH response per the "update the
  // entry using the API response, including editedAt" requirement, so the
  // data is correct and available the moment a future UI needs it.
  const [, setLastEditedInfo] = useState<
    Pick<FeedbackThreadEntryPatchResponse, 'editedAt' | 'edited'>
  >({ editedAt: null, edited: false });

  function handleStartEditing() {
    if (!editable) return;
    setDraftContent(committedContent);
    setIsEditing(true);
  }

  function handleCancel() {
    setIsEditing(false);
  }

  async function handleSave() {
    if (!canSave) return;
    const previousContent = committedContent;
    const newContent = draftContent;

    // Optimistic close + update - see this component's own doc comment on
    // why this mirrors Overview's rich-text field Save exactly.
    setCommittedContent(newContent);
    setIsEditing(false);
    setIsSaving(true);

    try {
      const response = await patchFeedbackThreadEntry(entryId, newContent);
      setCommittedContent(response.content);
      setLastEditedInfo({
        editedAt: response.editedAt ?? null,
        edited: response.edited ?? true
      });
    } catch (error) {
      setCommittedContent(previousContent);
      // A 401/403 already got its own toast from handle401Error/
      // handle403Error inside patchFeedbackThreadEntry (see
      // AlreadyNotifiedError's own comment) - showing a second one here for
      // the same failure would duplicate it. Content still reverts and
      // isSaving still clears either way.
      if (!(error instanceof AlreadyNotifiedError)) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to save feedback. Please try again.';
        ToastQueue.negative(message, { timeout: 5000 });
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className={classNames(styles.entryCard, {
        [styles.entryCardBordered]: bordered,
        [styles.entryCardStacked]: stacked
      })}
    >
      <div className={styles.entryLabelColumn}>
        <Text
          UNSAFE_className={classNames(styles.columnLabel, {
            [styles.stackedLabel]: stacked
          })}
        >
          {label}
        </Text>
        <div className={styles.entryDivider} />
        <div className={styles.entryAuthorRow}>
          <div className={styles.entryAvatar}>
            <Avatar
              src={getEmployeePhotoUrl(author.uid)}
              alt={author.name}
              name={author.name}
              size={32}
            />
          </div>
          <div className={styles.entryAuthorInfo}>
            <Text UNSAFE_className={styles.entryAuthorName}>{author.name}</Text>
            <Text UNSAFE_className={styles.entryAuthorRole}>{author.role}</Text>
          </div>
        </div>
      </div>
      <div
        className={classNames(styles.entryContent, {
          [styles.entryContentEditable]: editable && !isEditing
        })}
        onClick={editable && !isEditing ? handleStartEditing : undefined}
      >
        {isEditing ? (
          <>
            <div
              className={formFieldStyles.quillWrapper}
              style={{ display: 'flex' }}
            >
              <QuillEditor
                value={draftContent}
                onChange={setDraftContent}
                minHeight="100px"
                showCharCount={false}
                autoFocus
                className={formFieldStyles.fullEditor}
              />
            </div>
            <div className={styles.entryEditFooter}>
              <button
                type="button"
                className={styles.entryCancelButton}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.sendButton}
                onClick={handleSave}
                disabled={!canSave}
              >
                Save
              </button>
            </div>
          </>
        ) : (
          <ReadMoreContent content={committedContent}>
            <div
              className={classNames(styles.entryContentHtml, {
                [styles.entryContentHtmlEditable]: editable
              })}
              dangerouslySetInnerHTML={{ __html: committedContent }}
            />
          </ReadMoreContent>
        )}
        <Text UNSAFE_className={styles.entrySentOn}>
          Sent on: {moment(sentAt).format('MMMM D, YYYY h:mmA')}
        </Text>
      </div>
    </div>
  );
}

export default FeedbackThreadEntryCard;
